import {
  Box, Card, CardContent, Typography, Divider, Container, Paper,
} from '@mui/material'

const DocsPage = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box component="img" src="/brand/logo-horizontal.svg" alt="ARIAS" sx={{ height: 36, width: 'auto', display: 'block', mb: 3 }} />
      <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.01em', mb: 1 }}>Documentation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Getting started with ARIAS — the control plane for AI agents.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Quick Start</Typography>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>1. Install the Scanner</Typography>
          <Paper sx={{ p: 1.5, bgcolor: 'grey.900', borderRadius: 1, mt: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: 'monospace', color: 'grey.100', fontSize: '0.9rem' }}>
              curl -sL https://app.tryarias.com/install | sh
            </Typography>
          </Paper>

          <Typography variant="subtitle2">2. Run Your First Scan</Typography>
          <Paper sx={{ p: 1.5, bgcolor: 'grey.900', borderRadius: 1, mt: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: 'monospace', color: 'grey.100', fontSize: '0.9rem' }}>
              arias-scan --token &lt;SETUP_TOKEN&gt; /path/to/your/repo
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            The setup token is provided when you provision a scanner in the ARIAS dashboard.
            It is a one-time use token that activates the scanner and downloads its configuration.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>3. Subsequent Scans</Typography>
          <Paper sx={{ p: 1.5, bgcolor: 'grey.900', borderRadius: 1, mt: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: 'monospace', color: 'grey.100', fontSize: '0.9rem' }}>
              arias-scan /path/to/your/repo
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            After initial activation, no token is needed. The scanner uses the saved configuration.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>What the Scanner Does</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The ARIAS scanner runs entirely on your machine. It analyzes your codebase for AI agent
            patterns and transmits only structured metadata — never source code.
          </Typography>
          <Typography variant="subtitle2" gutterBottom>What is transmitted:</Typography>
          <Box component="ul" sx={{ pl: 2.5, mt: 0.5 }}>
            <Typography component="li" variant="body2" color="text.secondary">Framework names and versions detected</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Agent metadata: role, model, tools, guardrails, autonomy level</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Behavioral fingerprint hashes (ABF)</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Finding titles, severities, and anonymized file paths</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Scan metrics (file count, duration, agent count)</Typography>
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>What is NOT transmitted:</Typography>
          <Box component="ul" sx={{ pl: 2.5, mt: 0.5 }}>
            <Typography component="li" variant="body2" color="text.secondary">Source code or file contents</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Environment variables or secrets</Typography>
            <Typography component="li" variant="body2" color="text.secondary">Full file paths (paths are anonymized)</Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Supported Frameworks (27+)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Orchestration:</strong> LangChain, LangGraph, AutoGen, CrewAI, OpenAI Assistants,
            Anthropic Claude, Semantic Kernel, Haystack, LlamaIndex, DSPy, PydanticAI, SmolAgents, AutoGPT
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Cloud &amp; Enterprise:</strong> AWS Bedrock, Azure AI, Google Vertex, Databricks,
            Salesforce Einstein, ServiceNow, Snowflake Cortex
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Specialized:</strong> NVIDIA NeMo, NVIDIA NIM, Agno, n8n, ControlFlow, PromptFlow
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Native:</strong> Direct OpenAI/Anthropic API calls with tool use, custom pipelines,
            iterative agent loops
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>CI/CD Integration</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add ARIAS to your pipeline to scan on every push and block deployments when agent
            behavior drifts from what was certified.
          </Typography>
          <Typography variant="subtitle2">GitHub Actions</Typography>
          <Paper sx={{ p: 1.5, bgcolor: 'grey.900', borderRadius: 1, mt: 1, mb: 2 }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', color: 'grey.100', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
{`- name: Install ARIAS Scanner
  run: curl -sL https://app.tryarias.com/install | sh

- name: Run ARIAS scan
  run: arias-scan \${{ github.workspace }}

- name: Check ABF gate
  run: |
    # The gate requires the same scanner API key the scan uses.
    # Optional: ?block_on=critical  ?agents=<id-or-name>  ?fail_closed=true
    RESULT=$(curl -s -H "X-API-Key: \${{ secrets.ARIAS_API_KEY }}" \\
      https://tryarias.com/api/analytics/abf/gate/tenant/\$TENANT_ID)
    echo "$RESULT" | jq '.pass, .basis, .agents'
    # Remove the next line to run in monitor mode (report, never block).
    #
    # .basis.evidence_backed matters as much as .pass: the gate passes when it
    # has nothing to say — no certificates yet, no registered agents, no prior
    # scan, or an ?agents= filter that matched nothing — and blocking on .pass
    # alone treats every one of those as success.
    echo "$RESULT" | jq -e '.pass and .basis.evidence_backed' || exit 1`}
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Frequently Asked Questions</Typography>

          <Typography variant="subtitle2" sx={{ mt: 2 }}>Does ARIAS see my source code?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No. The scanner runs entirely on your machine or CI runner. It analyzes your code locally
            and transmits only structured metadata — framework names, agent attributes, behavioral
            fingerprint hashes, and anonymized findings. Source code never leaves your environment.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">How are agents counted across branches?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ARIAS counts distinct agents per repository based on the most recent scan. If you scan
            the same repo from different branches (main, develop, feature/*), only the latest scan's
            agent count is used — the same agent in multiple branches is not double-counted. Different
            repositories are counted separately since they represent independently governed agent deployments.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">Can I use ARIAS in CI/CD without blocking deployments?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Yes. By default, the CI/CD integration runs in alert mode — it reports drift status without
            failing the pipeline. You can opt into block mode by uncommenting a single line in the
            workflow file to fail the build when agent behavior drifts from what was certified.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">What is an Agent Behavioral Fingerprint (ABF)?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ABF is a behavioral fingerprint that captures what an agent is instructed to do,
            which tools it can use, how it accesses memory, how it coordinates with other agents, and
            how robust its error handling is. Each dimension is hashed independently so ARIAS can detect
            exactly which aspect changed between scans and classify drift as cosmetic, material, or critical.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">What frameworks does ARIAS support?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ARIAS detects agents across 27+ frameworks including LangChain, CrewAI, AutoGen, OpenAI,
            Anthropic, Semantic Kernel, Haystack, LlamaIndex, AWS Bedrock, Azure AI, and more. It also
            detects native agents built without frameworks — direct API calls with tool use, custom
            pipelines, and iterative loops.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">How does the maturity score work?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every scan produces a score across six dimensions: Prompt Engineering, Agent Design,
            Memory Architecture, Orchestration Soundness, Observability, and Governance Alignment.
            Scores are derived from matching your scan findings against 1,360+ knowledge base rules —
            not self-assessment surveys. Each matched rule applies a penalty to the relevant dimension.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">Can multiple team members access the dashboard?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Yes. The tenant admin can add team members from the Team page. Four roles are available:
            Viewer (read-only), Analyst (run scans), Manager (manage scanners), and Admin (full access
            including team management). All team members must use the same company email domain.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">What programming languages does ARIAS support?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ARIAS detects AI agents in Python, JavaScript/TypeScript, C#, and Java. This covers
            the major agent frameworks: LangChain, CrewAI, AutoGen, Semantic Kernel (.NET),
            OpenAI, Anthropic, AWS Bedrock, Azure AI, Google Vertex, Cloudflare Agents, and more.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">What is an assurance grade?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ARIAS assigns each agent an assurance grade from 1 (highest risk) to 5 (lowest risk)
            based on its capabilities, autonomy level, guardrails, and human oversight. Grade 1 agents
            are autonomous with code execution and no human gate. Grade 5 agents are basic responders
            with no tools. The grade helps teams prioritize governance effort where it matters most.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">What does ARIAS analyze beyond agent detection?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Beyond detecting agents, ARIAS performs deep analysis at four levels: tool quality
            (are tool descriptions clear? do they validate inputs? do they log actions?), prompt
            quality (are boundaries defined? injection defenses present?), model configuration
            (is temperature appropriate? are token limits set?), and guardrail effectiveness
            (are guardrails actually applied or just imported?).
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">Can I create custom governance rules?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Yes, on the Business plan and above. Custom rules let you codify your organization&apos;s
            specific standards — for example, &quot;all agents must use our internal LLM gateway&quot;
            or &quot;agents handling PII must have output filtering.&quot; Custom rules integrate into
            the same scoring engine as the 1,360+ built-in rules.
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">What compliance frameworks does ARIAS map to?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ARIAS maps scan findings to OWASP LLM Top 10 (all tiers), EU AI Act, NIST AI RMF,
            ISO 42001, GDPR, and SOC 2 (Business tier and above). Enterprise clients can add
            custom compliance frameworks for industry-specific standards like DO-178C (aerospace),
            FDA SaMD (healthcare), or SR 11-7 (financial services).
          </Typography>

          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2">Can I export data for my GRC tool?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enterprise tier includes ABF export (JSON and CSV) with certificates, drift events,
            and summary statistics — designed for import into GRC platforms like ServiceNow,
            Archer, or OneTrust. Bulk data export is also available for all analytics data.
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Support</Typography>
          <Typography variant="body2" color="text.secondary">
            Contact us at <strong>support@tryarias.com</strong>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  )
}

export default DocsPage
